import React, { useMemo } from "react";
import { noop, isEmpty } from "lodash";
import { UnaryFn, NullaryFn } from "@tvg/ts-types/Functional";
import Icon from "@fdr/static-ui/Icons";
import { arrowRight } from "@fdr/static-ui/Icons/icons";
import buildColor from "@fdr/static-ui/ColorPalette";
import MagnetScrollArea from "@fdr/atomic-ui/_templates/MagnetScrollArea";
import VideoCard, { VideoCardProps } from "../../_molecules/VideoCard";
import {
  Container,
  LinkContainer,
  IconContainer,
  LastElement,
  LastElementContainer,
  NoVideosContainer,
  VideoCardOutterContainer
} from "./styled-components";

export interface VideoPlaylistProps {
  videos: VideoCardProps[];
  onHandleTransition?: UnaryFn<number, void>;
  onCardClick?: UnaryFn<VideoCardProps, void>;
  activeIndex?: number;
  isLoading?: boolean;
  showLastCard?: boolean;
  onLastElementClick?: NullaryFn<void>;
  lastElementText?: string;
}

function VideoPlaylist({
  videos = [],
  onHandleTransition = noop,
  onCardClick = noop,
  activeIndex = 0,
  isLoading = false,
  showLastCard = false,
  onLastElementClick = noop,
  lastElementText
}: VideoPlaylistProps) {
  const renderVideos = () =>
    videos.map(({ video, description }) => (
      <VideoCardOutterContainer key={`videoCard-${video.playerId}`}>
        <VideoCard {...{ video, description }} onClick={onCardClick} />
      </VideoCardOutterContainer>
    ));

  const renderSeeMoreLink = () => (
    <LinkContainer
      onClick={onLastElementClick}
      data-qa-label="videoPlaylistLastElement-Link"
    >
      <IconContainer data-qa-label="videoPlaylistLastElement-IconContainer">
        <Icon
          icon={arrowRight}
          color={buildColor("blue", "D1")}
          size={24}
          qaLabel="videoPlaylistLastElement-Icon"
        />
      </IconContainer>
      {lastElementText}
    </LinkContainer>
  );

  const noVideos = !isLoading && videos.length === 0;

  return useMemo(
    () => (
      <Container data-qa-label="videoPlaylistContainer">
        {noVideos ? (
          <NoVideosContainer>{renderSeeMoreLink()}</NoVideosContainer>
        ) : (
          <MagnetScrollArea
            onHandleTransition={onHandleTransition}
            activeIndex={activeIndex}
            isLoading={isLoading}
          >
            {renderVideos()}
            {showLastCard || isEmpty(videos) ? (
              <LastElementContainer
                key="videoPlaylistLastElement"
                isFirstElement={!videos.length}
                data-qa-label="videoPlaylistLastElement"
              >
                <LastElement>{renderSeeMoreLink()}</LastElement>
              </LastElementContainer>
            ) : (
              []
            )}
          </MagnetScrollArea>
        )}
      </Container>
    ),
    [videos, isLoading]
  );
}

export default VideoPlaylist;
